import { deriveWorkState, type UserRole } from '@myapp/contract';
import { COMPLETED_RESCHEDULE_ROLES } from '../../../shared/hooks/useUserRoles';
import type { SegmentHandle, SpanHandle } from '../../../shared/lib/schedule';
import type { BoardBlock } from './boardTypes';

/**
 * What a block lets you do, per kind. Pure, no React.
 *
 * `apps/web/CLAUDE.md` forbids reintroducing the deleted timeline's three parallel drag
 * implementations. So there is one drag primitive and this file is the per-kind policy it consults
 * — the only place to look when a block refuses to move.
 */

/** Which boundary a resize grabs. `start`/`end` are the absence SPAN; the rest are segments. */
export type Handle = SegmentHandle | SpanHandle;

export type DragBlocker =
  | 'no-owner'
  | 'multi-technician'
  | 'clipped'
  | 'completed-forbidden'
  /** A session is open on it — the technician is driving, working or driving back right now. */
  | 'in-progress';

export interface DragPolicy {
  canMove: boolean;
  /** Cross-row drag, i.e. sending `technicianId`. */
  canReassign: boolean;
  /** Empty means move-only. */
  handles: Handle[];
  /**
   * Occupancy is `COALESCE(actual, planned)` per component, so a completed block is drawn from its
   * actuals — patching `slot` would leave it visually motionless. The patch is then the four
   * Ist-Zeitpunkte, which is also why a *running* block is inert rather than writing actuals: its
   * end is not known yet, and sending one would close the session from the board.
   */
  writesActuals: boolean;
  blockedBy: DragBlocker | null;
}

export interface DragContext {
  userRoles: UserRole[];
}

const COMPLETED_STATUSES = new Set(['COMPLETED', 'COMPLETED_WITH_FOLLOW_UP']);

const inert = (blockedBy: DragBlocker | null): DragPolicy => ({
  canMove: false,
  canReassign: false,
  handles: [],
  writesActuals: false,
  blockedBy,
});

export function resolveDragPolicy(block: BoardBlock, ctx: DragContext): DragPolicy {
  const { entry, span } = block;

  switch (entry.type) {
    case 'BREAK':
      // Move only: the duration is `TechnicianProfile.breakDurationMinutes`, not per-entry, so
      // there is nothing to resize. Cross-row is refused because
      // `schedule_entries_one_break_per_technician_day` would raise a DB error, not a domain one.
      // A projected break (`id: null`) is still movable — it goes through PUT /schedule/break.
      return {
        canMove: true,
        canReassign: false,
        handles: [],
        writesActuals: false,
        blockedBy: null,
      };

    case 'ABSENCE': {
      if (entry.absenceId == null) return inert('no-owner');
      // Dragging one technician's block re-reserves the absence for everyone on it, and sending
      // `technicianId` would collapse the roster to one. Neither is a safe thing to do by accident.
      if (block.technicianCount > 1) return inert('multi-technician');
      // A multi-day Urlaub extends past both edges of the day; dragging what you cannot see is
      // guesswork, and its real extent belongs in the Zeitmanagement form.
      if (span.clippedStart || span.clippedEnd) return inert('clipped');

      return {
        canMove: true,
        canReassign: true,
        handles: ['start', 'end'],
        writesActuals: false,
        blockedBy: null,
      };
    }

    case 'APPOINTMENT':
    case 'TASK': {
      const ownerId = entry.type === 'APPOINTMENT' ? entry.appointmentId : entry.taskId;
      if (ownerId == null) return inert('no-owner');

      // One rule for both kinds now. The task list used to be narrower here, mirroring an API that
      // 403'd a MANAGER on a completed task; both were widened together.
      if (
        block.status != null &&
        COMPLETED_STATUSES.has(block.status) &&
        !ctx.userRoles.some((role) => COMPLETED_RESCHEDULE_ROLES.includes(role))
      ) {
        // The server would 403. Better to look immovable than to snap back every time.
        return inert('completed-forbidden');
      }

      // Anfahrt, Arbeit or Abfahrt is running on it. The only patch that could move such a block
      // is the four Ist-Zeitpunkte, and one of them — the end of the open phase — does not exist
      // yet; inventing it from a drag would sign the technician off. The block follows the clock
      // on its own (`resolveBlockTimes` live edges) and moves again once the session is closed.
      if (deriveWorkState({ status: block.status ?? 'PLANNED', schedule: entry }).isRunning) {
        return inert('in-progress');
      }

      // A move is a delta, so it stays meaningful on a clipped block. Resizing an edge that runs
      // off the window is not — you would be dragging a boundary you cannot see.
      const handles: Handle[] = [];
      if (!span.clippedStart) handles.push('travel');
      handles.push('work');
      if (!span.clippedEnd) handles.push('departure');

      return {
        canMove: true,
        canReassign: true,
        handles,
        writesActuals: entry.actualWorkStartsAt != null,
        blockedBy: null,
      };
    }

    default:
      return inert(null);
  }
}
